/**
 * Taste Graph v1 — signal collection utilities
 *
 * Persists thumbs up/down signals and save events so they survive
 * across sessions and devices for logged-in users.
 *
 * Architecture:
 *  - localStorage is always written (fast reads, works for anon users)
 *  - Supabase is written in parallel for logged-in users (fire-and-forget)
 *  - interaction_events: immutable log — never updated, only inserted
 *  - user_taste_signals: latest signal per (user, place) — fast lookup
 */

import { supabase } from './supabase';
import { safeStorage } from './safeStorage';
import { reportError } from './reportError';

export type SignalType = 'like' | 'dislike';
export type TasteEventType = 'like' | 'dislike' | 'unlike' | 'undislike' | 'save' | 'share';

export interface PlaceMeta {
  name?: string;
  types?: string[];
  category?: string;
  matchingVibes?: string[];
}

/** Local key for a place signal in localStorage */
function signalKey(placeId: string): string {
  return `place_signal_${placeId}`;
}

/**
 * Read the locally-cached signal for a place.
 * Fast path — used to initialise thumbs state without a DB round-trip.
 */
export function getLocalSignal(placeId: string): SignalType | null {
  const s = safeStorage.getItem(signalKey(placeId));
  return s === 'like' || s === 'dislike' ? s : null;
}

/**
 * Set (or clear) a thumbs signal for a place.
 * Writes to localStorage immediately; syncs to Supabase in the background.
 */
export function setTasteSignal(
  userId: string | null,
  placeId: string,
  signal: SignalType | null
): void {
  // ── 1. localStorage (sync, instant) ──────────────────────────
  if (signal === null) {
    safeStorage.remove(signalKey(placeId));
  } else {
    safeStorage.setItem(signalKey(placeId), signal);
  }

  // ── 2. Supabase (async, fire-and-forget) ─────────────────────
  if (!userId || !supabase) return;

  // Supabase queries return a result with `.error` on the payload; they
  // do NOT reject. The previous code chained `.catch()` on a PromiseLike
  // that doesn't have a `.catch` method, so it was silently dead for
  // every failing DB write. Fixed H-104b.
  if (signal === null) {
    supabase
      .from('user_taste_signals')
      .delete()
      .eq('user_id', userId)
      .eq('place_id', placeId)
      .then((res) => {
        if (res.error) reportError('tasteGraph.signalDelete', res.error, { placeId });
      });
  } else {
    supabase
      .from('user_taste_signals')
      .upsert({ user_id: userId, place_id: placeId, signal, updated_at: new Date().toISOString() })
      .then((res) => {
        if (res.error) reportError('tasteGraph.signalUpsert', res.error, { placeId, signal });
      });
  }
}

/**
 * Append an entry to the immutable interaction event log.
 * Silently no-ops for anonymous users or when Supabase is not configured.
 */
export function recordTasteEvent(
  userId: string | null,
  placeId: string,
  eventType: TasteEventType,
  meta: PlaceMeta = {}
): void {
  if (!userId || !supabase) return;

  supabase
    .from('interaction_events')
    .insert({
      user_id: userId,
      place_id: placeId,
      event_type: eventType,
      place_name: meta.name ?? null,
      place_types: meta.types ?? null,
      place_category: meta.category ?? null,
      matching_vibes: meta.matchingVibes ?? null,
    })
    .then((res) => {
      if (res.error) reportError('tasteGraph.event', res.error, { placeId, eventType });
    });
}
